import React, { useState, useRef, useEffect, useCallback } from 'react';
import './CameraCapture.css';

interface CameraCaptureProps {
  isOpen: boolean;
  onCapture: (blob: Blob) => void;
  onClose: () => void;
}

export const CameraCapture: React.FC<CameraCaptureProps> = ({
  isOpen,
  onCapture,
  onClose,
}) => {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user');
  const [hasMultipleCameras, setHasMultipleCameras] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const stopStream = useCallback((s: MediaStream | null) => {
    s?.getTracks().forEach(t => t.stop());
  }, []);

  /* Detect multiple cameras */
  useEffect(() => {
    if (!isOpen) return;
    navigator.mediaDevices.enumerateDevices()
      .then(devices => {
        const videoInputs = devices.filter(d => d.kind === 'videoinput');
        setHasMultipleCameras(videoInputs.length > 1);
      })
      .catch(() => setHasMultipleCameras(false));
  }, [isOpen]);

  /* Start/restart camera when open or facingMode changes */
  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;

    const start = async () => {
      try {
        // Use 'exact' to force front/back camera on mobile.
        // Fall back to ideal hint if exact fails (e.g. desktop with one camera).
        let newStream: MediaStream;
        try {
          newStream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: { exact: facingMode } },
          });
        } catch {
          newStream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode },
          });
        }
        if (cancelled) {
          newStream.getTracks().forEach(t => t.stop());
          return;
        }
        setStream(prev => {
          stopStream(prev);
          return newStream;
        });
        setError(null);
        if (videoRef.current) {
          videoRef.current.srcObject = newStream;
        }
      } catch {
        if (!cancelled) setError('Camera access denied');
      }
    };

    start();
    return () => { cancelled = true; };
  }, [isOpen, facingMode, stopStream]);

  /* Attach stream to video element when ref becomes available */
  useEffect(() => {
    if (videoRef.current && stream) {
      videoRef.current.srcObject = stream;
    }
  }, [stream]);

  /* Cleanup on close / unmount */
  useEffect(() => {
    if (!isOpen && stream) {
      stopStream(stream);
      setStream(null);
      setError(null);
    }
  }, [isOpen, stream, stopStream]);

  useEffect(() => {
    return () => { stopStream(stream); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isOpen) return null;

  const handleCapture = () => {
    if (!videoRef.current) return;
    setFlash(true);
    setTimeout(() => setFlash(false), 120);
    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    if (facingMode === 'user') {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }
    ctx.drawImage(video, 0, 0);

    canvas.toBlob((blob) => {
      if (blob) {
        stopStream(stream);
        setStream(null);
        onCapture(blob);
      }
    }, 'image/jpeg', 0.9);
  };

  const handleClose = () => {
    stopStream(stream);
    setStream(null);
    onClose();
  };

  const handleFlip = () => {
    setFacingMode(m => m === 'user' ? 'environment' : 'user');
  };

  return (
    <div className="cc-overlay">
      {flash && <div className="cc-flash" />}
      <button className="cc-cancel" onClick={handleClose}>Cancel</button>

      {error ? (
        <div className="cc-error">
          <div className="cc-error-text">{error}</div>
          <button className="cc-error-btn" onClick={handleClose}>Go Back</button>
        </div>
      ) : (
        <>
          <video
            ref={videoRef}
            className="cc-video"
            autoPlay
            playsInline
            muted
            style={{ transform: facingMode === 'user' ? 'scaleX(-1)' : 'none' }}
          />

          <div className="cc-controls">
            {/* Spacer for centering */}
            <div className="cc-spacer" />

            <button className="cc-capture-btn" onClick={handleCapture}>
              <div className="cc-capture-inner" />
            </button>

            {hasMultipleCameras ? (
              <button className="cc-flip-btn" onClick={handleFlip}>
                <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
                  <path d="M16 3h5v5M8 21H3v-5M21 3l-7 7M3 21l7-7" />
                </svg>
              </button>
            ) : (
              <div className="cc-spacer" />
            )}
          </div>
        </>
      )}
    </div>
  );
};
